/**
 * Colonnes d'un tableau custom (non variable) réglées depuis l'écran
 * « Configurer le tableau ». Contrairement à un tableau variable, rien n'y est
 * une surcouche de présentation : largeurs (en px), ordre et intitulés sont
 * ceux de `box.content.columns`, seule source du tableau.
 *
 * Module partagé : pur, sans React ni store.
 */

const sum = (values) => values.reduce((total, value) => total + value, 0);

// Indices (dans le désordre) qui se suivent sans trou.
const isContiguous = (indexes) => {
  const sorted = [...indexes].sort((a, b) => a - b);
  return sorted.every((idx, i) => i === 0 || idx === sorted[i - 1] + 1);
};

/**
 * Largeur de chaque colonne en pourcent de la largeur du tableau, arrondie à
 * l'unité comme dans l'écran de configuration ; la dernière prend le reste
 * pour que le total fasse 100.
 */
export const columnWidthPercents = (columns) => {
  if (!columns?.length) return [];
  const total = sum(columns.map((col) => col.width || 0));
  const percents = columns.map((col) => (
    total > 0 ? Math.round(((col.width || 0) / total) * 100) : Math.round(100 / columns.length)
  ));
  percents[percents.length - 1] = 100 - sum(percents.slice(0, -1));
  return percents;
};

/**
 * Largeurs (px) après avoir donné `percent` % à la colonne `key` : la largeur
 * du tableau ne bouge pas, la dernière colonne absorbe l'écart (comme
 * setColumnWidth d'un tableau variable). Aucune colonne ne passe sous
 * `minWidth` ; sans place pour la changer, les largeurs restent les mêmes.
 */
export const setColumnWidthPercent = (columns, key, percent, minWidth = 0) => {
  const widths = (columns ?? []).map((col) => col.width || 0);
  const idx = (columns ?? []).findIndex((col) => col.key === key);
  const last = widths.length - 1;
  const total = sum(widths);
  if (idx === -1 || idx === last || total <= 0) return widths;
  const othersSum = sum(widths.filter((_, i) => i !== idx && i !== last));
  const max = total - othersSum - minWidth;
  if (max < minWidth) return widths;
  // Pas d'arrondi au pixel : le canvas affiche le pourcentage au centième.
  const target = Math.min(Math.max((percent / 100) * total, minWidth), max);
  const next = [...widths];
  next[idx] = target;
  next[last] = total - othersSum - target;
  return next;
};

const leafKeys = (node) => (node.children ? node.children.flatMap(leafKeys) : [node.key]);

/**
 * Groupes d'en-têtes (`columnsDisposition`) remis dans l'ordre `orderedKeys`
 * des colonnes : l'en-tête suit l'ordre de la disposition, le corps celui des
 * colonnes, les deux doivent concorder. Un groupe dont les colonnes ne se
 * suivent plus est défait (ses colonnes remontent d'un niveau) ; sans groupe
 * restant, [] : l'en-tête retombe sur les colonnes (cf. ungroupColumns).
 */
export const reorderColumnsDisposition = (columnsDisposition, orderedKeys) => {
  if (!Array.isArray(columnsDisposition) || columnsDisposition.length === 0) return columnsDisposition;
  const position = new Map(orderedKeys.map((key, idx) => [key, idx]));
  const positionsOf = (node) => leafKeys(node).map((key) => position.get(key)).filter((idx) => idx !== undefined);
  const first = (node) => Math.min(...positionsOf(node));
  const arrange = (nodes) => nodes
    .flatMap((node) => {
      if (!node.children) return [node];
      const children = arrange(node.children);
      const grouped = { ...node, children };
      return isContiguous(positionsOf(grouped)) ? [grouped] : children;
    })
    .sort((a, b) => first(a) - first(b));
  const next = arrange(columnsDisposition);
  return next.some((node) => node.children) ? next : [];
};

/**
 * Fusions de cellules (`mergedCells`) qui restent des rectangles une fois les
 * colonnes dans l'ordre `orderedKeys`. Une fusion traversée par une colonne
 * déplacée disparaît, comme celle d'une ligne déplacée au canvas : sans ça,
 * elle reviendrait toute seule en remettant la colonne à sa place. Une fusion
 * qui vise une colonne supprimée est laissée telle quelle (déjà ignorée au rendu).
 */
export const keepContiguousColumnMerges = (mergedCells, orderedKeys) => {
  if (!Array.isArray(mergedCells)) return mergedCells;
  const position = new Map(orderedKeys.map((key, idx) => [key, idx]));
  return mergedCells.filter((merge) => {
    const indexes = (merge.columnKeys ?? []).map((key) => position.get(key));
    return indexes.some((idx) => idx === undefined) || isContiguous(indexes);
  });
};

/**
 * Colonnes, groupes d'en-têtes et fusions d'un tableau custom une fois ses
 * colonnes dans l'ordre `orderedKeys`.
 */
export const reorderCustomTableColumns = (content, orderedKeys) => {
  const byKey = new Map((content.columns ?? []).map((col) => [col.key, col]));
  const columns = orderedKeys.map((key) => byKey.get(key)).filter(Boolean);
  const keys = columns.map((col) => col.key);
  return {
    columns,
    columnsDisposition: reorderColumnsDisposition(content.columnsDisposition, keys),
    mergedCells: keepContiguousColumnMerges(content.mergedCells, keys),
  };
};
